import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  VStack,
  HStack,
  Text,
  Checkbox,
  CheckboxIndicator,
  CheckboxIcon,
  CheckIcon,
  Spinner,
  Button,
  ButtonText,
  Alert,
  AlertText,
  ScrollView,
} from '@gluestack-ui/themed';
import {
  useGetAvailableNotificationKinds,
  getGetAvailableNotificationKindsQueryKey,
} from '../../../../common/api/endpoints/users/users';
import SettingsGroupCard from '../../../../common/components/forms/SettingsGroupCard';

const groupByCategory = (kinds) => {
  const groups = kinds.reduce((acc, k) => {
    const category = k.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(k);
    return acc;
  }, {});
  const sortedCategories = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  return sortedCategories.map((cat) => ({
    category: cat,
    items: groups[cat].sort((a, b) => a.code.localeCompare(b.code)),
  }));
};

const UserNotificationsForm = () => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();

  const userId = watch('id');
  const selectedRole = watch('role');

  const roleScopedQueryKey = React.useMemo(() => {
    const base = getGetAvailableNotificationKindsQueryKey(userId);
    return [...base, { role: selectedRole || null }];
  }, [userId, selectedRole]);

  const {
    data, isLoading, isError, refetch,
  } = useGetAvailableNotificationKinds(userId, {
    query: { queryKey: roleScopedQueryKey },
    axios: { params: { role: selectedRole } },
  });

  const response = data?.data?.data || null;
  const channels = response?.channels || [];

  const kinds = React.useMemo(() => response?.kinds || [], [response]);

  const emailPreferences = watch('notificationPrefs.email.preferences');

  React.useEffect(() => {
    // If form has no preferences for this channel, and we have them from the catalog, seed them.
    if (!emailPreferences || Object.keys(emailPreferences).length === 0) {
      const responsePrefs = response?.preferences?.email;
      if (responsePrefs) {
        setValue('notificationPrefs.email.preferences', responsePrefs, { shouldDirty: false });
      }
    }
  }, [emailPreferences, response, setValue]);

  const emailEditable = channels.includes('email');

  const toggleCode = (code, checked) => {
    const next = { ...(emailPreferences || {}), [code]: checked };
    setValue('notificationPrefs.email.preferences', next, { shouldDirty: true });
  };

  if (!userId) {
    return (
      <Box p="$6">
        <Text size="sm">
          {t('notifications.unavailable_for_new_user', 'Notifications are available after creating the user.')}
        </Text>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <VStack p="$6" space="md">
        <HStack alignItems="center" space="sm">
          <Spinner />
          <Text size="sm">{t('loading', 'Loading')}</Text>
        </HStack>
      </VStack>
    );
  }

  if (isError) {
    return (
      <VStack p="$6" space="md">
        <Alert action="error">
          <AlertText>{t('notifications.load_failed', 'Failed to load notification kinds')}</AlertText>
        </Alert>
        <Button onPress={() => refetch()} variant="outline" w={140}>
          <ButtonText>{t('retry', 'Retry')}</ButtonText>
        </Button>
      </VStack>
    );
  }

  if (!kinds || kinds.length === 0) {
    return (
      <Box p="$6">
        <Text size="sm">{t('notifications.none_available', 'No notifications available for this user.')}</Text>
      </Box>
    );
  }

  const groups = groupByCategory(kinds);
  const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  return (
    <ScrollView flex={1} bg="$backgroundLight0">
      <VStack space="xl" py="$6" px="$8" alignItems="center">
        <VStack space="xl" width="100%" maxWidth={760}>
          {!emailEditable && (
            <Alert action="info">
              <AlertText>
                {t(
                  'notifications.email_not_editable',
                  'No editable email channel for this user. Preferences are read-only.',
                )}
              </AlertText>
            </Alert>
          )}

          <SettingsGroupCard title="Notification Preferences">
            <VStack space="xl">
              {groups.map(({ category, items }, groupIdx) => (
                <VStack key={category} space="md">
                  {/* Intermediate Headline */}
                  <Text size="sm" fontWeight="semibold" color="$textLight800" mt={groupIdx > 0 ? '$2' : 0}>
                    {capitalize(category)}
                  </Text>

                  <VStack space="md" pl="$1">
                    {items.map((k) => {
                      const { code } = k;
                      const labelKey = `notifications.kinds.${code}.label`;
                      const descKey = `notifications.kinds.${code}.description`;
                      const label = t(labelKey, k.description || code);
                      const desc = t(descKey, '');
                      const isChecked = emailPreferences ? !!emailPreferences[code] : false;
                      return (
                        <VStack key={code} space="xs">
                          <Checkbox
                            size="sm"
                            isDisabled={!emailEditable}
                            isReadOnly={!emailEditable}
                            isChecked={isChecked}
                            onChange={(checked) => toggleCode(code, checked)}
                          >
                            <CheckboxIndicator mr="$3">
                              <CheckboxIcon as={CheckIcon} />
                            </CheckboxIndicator>
                            <Text size="sm" color="$textLight700">
                              {label}
                            </Text>
                          </Checkbox>
                          {!!desc && (
                            <Text size="xs" color="$textLight500" ml="$8">
                              {desc}
                            </Text>
                          )}
                        </VStack>
                      );
                    })}
                  </VStack>

                  {/* Divider between sections except last one */}
                  {groupIdx < groups.length - 1 && (
                    <Box height={1} bg="$borderLight100" mt="$4" mb="$2" />
                  )}
                </VStack>
              ))}
            </VStack>
          </SettingsGroupCard>
        </VStack>
      </VStack>
    </ScrollView>
  );
};

export default UserNotificationsForm;
